export const DISPLAY_FONT = '"Kapra Neue", "Oswald", sans-serif';

export const TREND_COLORS = {
  pageBg: '#FAF6F1',
  cardBg: '#FFFFFF',
  sectionBg: '#F4F1EB',
  navy: '#072f53',
  navyLight: '#1f4b73',
  cream: '#FAF6F1',
  olive: '#7A8B5C',
  oliveLight: '#E8EDD9',
  terracotta: '#C4764E',
  terracottaLight: '#FBEEE6',
  gold: '#C9A96E',
  goldLight: '#FBF5E8',
  softNavy: '#E8EDF2',
  divider: '#E0DAD0',
  textPrimary: '#1A1A1A',
  textSecondary: '#5A5549',
  textMuted: '#8A867F',
  warningBg: '#FFF4D6',
  warningText: '#8A5D00',
  dangerBg: '#FDE8E8',
  dangerText: '#9B2C2C',
} as const;

// Shared series colors for charts so visualisations match the brand palette.
export const TREND_CHART_COLORS = {
  primary: TREND_COLORS.navy,
  secondary: TREND_COLORS.terracotta,
  grid: TREND_COLORS.divider,
} as const;

export const HEADING_SX = {
  fontFamily: DISPLAY_FONT,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
};

export const SECTION_HEADING_SX = {
  ...HEADING_SX,
  fontSize: '1.25rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  color: TREND_COLORS.navy,
  borderBottom: `2px solid ${TREND_COLORS.divider}`,
  pb: 1,
};

export const PAGE_HEADING_SX = {
  ...HEADING_SX,
  fontSize: '2.6rem',
  fontWeight: 700,
  letterSpacing: '0.1em',
  color: TREND_COLORS.navy,
};

export const CARD_TITLE_SX = {
  fontFamily: DISPLAY_FONT,
  textTransform: 'none' as const,
  letterSpacing: '0.01em',
  fontWeight: 700,
  color: TREND_COLORS.navy,
  lineHeight: 1.12,
};

export const STORY_CARD_TITLE_SX = {
  ...CARD_TITLE_SX,
  fontSize: { xs: '1.22rem', md: '1.34rem' },
};

export const ARTICLE_CARD_TITLE_SX = {
  ...CARD_TITLE_SX,
  fontSize: { xs: '1.08rem', md: '1.16rem' },
  lineHeight: 1.18,
};

export const CARD_INGRESS_SX = {
  color: TREND_COLORS.textSecondary,
  lineHeight: 1.72,
  fontSize: '0.95rem',
};

export const NAV_SX = {
  active: {
    bgcolor: TREND_COLORS.navy,
    color: '#fff',
    fontWeight: 700,
    '&:hover': { bgcolor: TREND_COLORS.navyLight },
  },
  inactive: {
    bgcolor: 'transparent',
    color: TREND_COLORS.navy,
    borderColor: TREND_COLORS.divider,
    '&:hover': { bgcolor: TREND_COLORS.softNavy },
  },
  admin: {
    bgcolor: 'transparent',
    color: TREND_COLORS.textMuted,
    borderColor: TREND_COLORS.divider,
    fontSize: '0.78rem',
    '&:hover': { bgcolor: TREND_COLORS.sectionBg },
  },
} as const;

export const CARD_RADIUS = 3;

export const CARD_HOVER_SX = {
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 24px rgba(7,47,83,0.10)',
  },
};

export const CATEGORY_FALLBACK_IMAGES: Record<string, string> = {
  food: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&h=450&fit=crop&auto=format',
  market: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=450&fit=crop&auto=format',
  consumer: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=450&fit=crop&auto=format',
  packaging: 'https://images.unsplash.com/photo-1607082349566-187342175e2f?w=800&h=450&fit=crop&auto=format',
  design: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&h=450&fit=crop&auto=format',
  competition: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=450&fit=crop&auto=format',
  mixed: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=800&h=450&fit=crop&auto=format',
  default: 'https://images.unsplash.com/photo-1506368249639-73a05d6f6488?w=800&h=450&fit=crop&auto=format',
};

function decodeTrendImageUrl(value: string): string {
  return String(value || '')
    .replace(/&amp;|&#0*38;|&#x0*26;/gi, '&')
    .replace(/&quot;|&#0*34;|&#x0*22;/gi, '"')
    .replace(/&apos;|&#0*39;|&#x0*27;/gi, '\'')
    .trim();
}

export function getFallbackArticleImage(category?: string): string {
  return CATEGORY_FALLBACK_IMAGES[category || ''] || CATEGORY_FALLBACK_IMAGES.default;
}

export function getArticleImage(heroImage?: string, category?: string): string {
  const normalizedHeroImage = decodeTrendImageUrl(heroImage || '');
  if (normalizedHeroImage && normalizedHeroImage.startsWith('http')) return normalizedHeroImage;
  return getFallbackArticleImage(category);
}

export function getDesignSubCategoryLabel(value?: string): string {
  const labels: Record<string, string> = {
    label_design: 'Label design',
    visual_identity: 'Visual identity',
    typography: 'Typografi',
    color_direction: 'Färgriktning',
    brand_expression: 'Varumärkesuttryck',
    shelf_communication: 'Hyllkommunikation',
    premium_cues: 'Premiummarkörer',
    packaging_structure: 'Förpackningsstruktur',
  };
  const normalized = String(value || '').trim();
  return labels[normalized] || normalized;
}
